/* global debounce */

/**
 * Remanx search normalisation.
 *
 * Shopify searches the literal indexed form of many part numbers and joined
 * vehicle makes. Build a short, ordered fallback list instead of forcing the
 * customer to know the exact punctuation or spacing used in the catalogue.
 */
const RMX_MULTIWORD_SEARCH_ALIASES = [
  { pattern: /\bland[\s._\-/]*rover\b/gi, value: 'land rover' },
  { pattern: /\brange[\s._\-/]*rover\b/gi, value: 'range rover' },
  { pattern: /\bmercedes[\s._\-/]*benz\b/gi, value: 'mercedes benz' },
  { pattern: /\balfa[\s._\-/]*romeo\b/gi, value: 'alfa romeo' },
  { pattern: /\baston[\s._\-/]*martin\b/gi, value: 'aston martin' },
  { pattern: /\brolls[\s._\-/]*royce\b/gi, value: 'rolls royce' },
  { pattern: /\bgreat[\s._\-/]*wall\b/gi, value: 'great wall' },
  { pattern: /\bds[\s._\-/]*automobiles\b/gi, value: 'ds automobiles' },
  { pattern: /\bvw\b/gi, value: 'volkswagen' }
];

function tidySearchTerm(str) {
  return (str || '').trim().replace(/\s+/g, ' ');
}

function addUniqueSearchCandidate(list, value) {
  const candidate = tidySearchTerm(value);
  if (!candidate) return;

  const key = candidate.toLowerCase();
  if (!list.some((item) => item.toLowerCase() === key)) list.push(candidate);
}

function normaliseVehicleMakeSpacing(str) {
  let value = tidySearchTerm(str);
  RMX_MULTIWORD_SEARCH_ALIASES.forEach((alias) => {
    value = value.replace(alias.pattern, alias.value);
  });
  return tidySearchTerm(value);
}

function looksLikePartNumber(str) {
  const value = tidySearchTerm(str);
  if (!/\d/.test(value)) return false;

  const compact = value.replace(/[^a-z0-9]/gi, '');
  const digitCount = (compact.match(/\d/g) || []).length;
  if (compact.length < 4 || digitCount < 3) return false;

  // Compact OEM references such as 61353456034 and 1K0907379AD are common on
  // Remanx. Shopify's native storefront search can require the full value for
  // uninterrupted codes, so these must still be recognised as part numbers.
  if (!/[\s._\-/]/.test(value)) {
    return compact.length >= 6 && digitCount / compact.length >= 0.35;
  }

  // Prevent normal vehicle phrases such as "BMW 3 Series" being treated as
  // part numbers while still allowing formats such as "A 000 446 12 89".
  return digitCount / compact.length >= 0.45;
}


const RMX_PART_SEARCH_MIN_CHARS = 6;
const RMX_FAULT_SEARCH_MIN_CHARS = 4;
const RMX_FAULT_SEARCH_MAX_CHARS = 8;
const RMX_PART_SEARCH_FULL_LIMIT = 60;
let rmxPartSearchIndexPromise = null;

function compactPartLookupValue(value) {
  return (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function shouldUseRemanxPartIndex(searchTerm) {
  const compact = compactPartLookupValue(searchTerm);
  return compact.length >= RMX_PART_SEARCH_MIN_CHARS && looksLikePartNumber(searchTerm);
}

function compactFaultLookupValue(value) {
  return (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function looksLikeFaultCode(searchTerm) {
  const value = tidySearchTerm(searchTerm);
  if (!value || /\s/.test(value)) return false;
  if (!/^[a-z0-9:._\/-]+$/i.test(value)) return false;

  const compact = compactFaultLookupValue(value);
  if (compact.length < RMX_FAULT_SEARCH_MIN_CHARS || compact.length > RMX_FAULT_SEARCH_MAX_CHARS) return false;
  if (!/\d/.test(compact)) return false;

  // This deliberately accepts common manufacturer/OBD patterns such as 5DF5,
  // P0606, C1234, U0100 and numeric codes. A product is only labelled as a
  // fault-code match after the exact code token is also found in its description.
  return true;
}

function shouldUseRemanxFaultIndex(searchTerm) {
  return looksLikeFaultCode(searchTerm);
}

function findFaultTokenPosition(description, searchTerm) {
  const query = compactFaultLookupValue(searchTerm);
  if (!query) return -1;

  const source = String(description || '');
  const tokenPattern = /[a-z0-9][a-z0-9:._\/-]{2,15}/gi;
  let match;

  while ((match = tokenPattern.exec(source))) {
    if (compactFaultLookupValue(match[0]) !== query) continue;

    // Short catalogue references can look code-like too. Confirm ambiguous
    // tokens from nearby diagnostic wording. Standard OBD-II patterns such as
    // P0606/C1234/U0100/B1000 are strong enough to stand on their own.
    const obdStyle = /^[pcbu]\d{4}$/i.test(query);
    if (!obdStyle) {
      const contextStart = Math.max(0, match.index - 140);
      const contextEnd = Math.min(source.length, match.index + match[0].length + 140);
      const context = source.slice(contextStart, contextEnd);
      if (!/(fault|dtc|error|diagnostic|diagnosis|trouble|stored code|common fault|fault code)/i.test(context)) {
        continue;
      }
    }

    return match.index;
  }

  return -1;
}

function escapeSearchHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function getRemanxRootUrl() {
  if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
    return window.Shopify.routes.root;
  }
  return '/';
}

async function fetchRemanxPartIndexPage(page) {
  const root = getRemanxRootUrl();
  const url = `${root}collections/all?section_id=remanx-part-search-index&page=${page}`;
  const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
  if (!response.ok) throw new Error(`Remanx part index ${response.status}`);

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const indexRoot = doc.querySelector('[data-rmx-part-search-index]');
  const dataEl = indexRoot && indexRoot.querySelector('[data-rmx-part-search-data]');
  if (!indexRoot || !dataEl) throw new Error('Remanx part index markup missing');

  let items = [];
  try {
    items = JSON.parse(dataEl.textContent || '[]');
  } catch (error) {
    throw new Error('Remanx part index JSON invalid');
  }

  return {
    items,
    pages: Math.max(1, Number(indexRoot.dataset.rmxIndexPages || 1))
  };
}

async function loadRemanxPartSearchIndex() {
  if (rmxPartSearchIndexPromise) return rmxPartSearchIndexPromise;

  rmxPartSearchIndexPromise = (async () => {
    const first = await fetchRemanxPartIndexPage(1);
    let items = first.items;

    // Current Remanx catalogue is under one 250-product page, but keep the
    // loader future-safe if the catalogue grows beyond that.
    if (first.pages > 1) {
      const pageCount = Math.min(first.pages, 20);
      const pageRequests = [];
      for (let page = 2; page <= pageCount; page += 1) {
        pageRequests.push(fetchRemanxPartIndexPage(page));
      }
      const remaining = await Promise.all(pageRequests);
      remaining.forEach((page) => {
        items = items.concat(page.items);
      });
    }

    return items.map((item) => ({
      ...item,
      _rmxCompactSearch: compactPartLookupValue(item.search),
      _rmxCompactTitle: compactPartLookupValue(item.title),
      _rmxFaultDescription: String(item.description || '')
    }));
  })().catch((error) => {
    // Permit a later retry after a transient network/Shopify section failure.
    rmxPartSearchIndexPromise = null;
    throw error;
  });

  return rmxPartSearchIndexPromise;
}

function findRemanxPartMatches(index, searchTerm) {
  const query = compactPartLookupValue(searchTerm);
  if (query.length < RMX_PART_SEARCH_MIN_CHARS) return [];

  return index
    .map((item) => {
      const haystack = item._rmxCompactSearch || '';
      const position = haystack.indexOf(query);
      if (position === -1) return null;

      // Title/SKU/tag content occurs before the description in the index blob,
      // so earlier occurrences naturally rank more specific catalogue data first.
      const titleHit = (item._rmxCompactTitle || '').includes(query) ? 0 : 1;
      return { item, position, titleHit };
    })
    .filter(Boolean)
    .sort((a, b) => a.titleHit - b.titleHit || a.position - b.position || a.item.title.localeCompare(b.item.title))
    .map((entry) => entry.item);
}

function findRemanxFaultMatches(index, searchTerm) {
  if (!shouldUseRemanxFaultIndex(searchTerm)) return [];

  return index
    .map((item) => {
      const position = findFaultTokenPosition(item._rmxFaultDescription, searchTerm);
      return position === -1 ? null : { item, position };
    })
    .filter(Boolean)
    .sort((a, b) => a.position - b.position || a.item.title.localeCompare(b.item.title))
    .map((entry) => entry.item);
}

function buildRemanxPartPredictiveMarkup(matches, searchTerm, limit) {
  const visible = matches.slice(0, limit);
  const total = matches.length;
  const items = visible.map((item) => {
    const image = item.image
      ? `<div class="predictive-result__media media relative"><img src="${escapeSearchHtml(item.image)}" alt="${escapeSearchHtml(item.image_alt || item.title)}" loading="eager" class="predictive-result__media"></div>`
      : '<div class="predictive-result__media media relative"></div>';
    const pricePrefix = item.price_varies ? 'From ' : '';

    return `
      <li class="predictive-search__item" role="option">
        <a class="predictive-result flex items-start focus-inset js-search-link" href="${escapeSearchHtml(item.url)}">
          ${image}
          <div class="predictive-result__info flex-auto">
            <h3 class="predictive-result__title h6">${escapeSearchHtml(item.title)}</h3>
            <div class="predictive-result__sub-title text-sm rmx-part-predictive__match">Part-number match</div>
            <div class="price rmx-part-predictive__price">${pricePrefix}${escapeSearchHtml(item.price)}</div>
          </div>
        </a>
      </li>`;
  }).join('');

  return `
    <div class="predictive-search absolute bg-theme-bg text-theme-text text-start rmx-part-predictive">
      <div class="predictive-search__scroller overflow-auto">
        <div class="predictive-search__results flex flex-col">
          <div class="rmx-part-predictive__head">
            <strong>Part-number matches</strong>
            <span>${total} found</span>
          </div>
          <div class="predictive-search__tabpanel focus-inset" role="tabpanel" tabindex="0">
            <ul class="predictive-search__list" role="list">
              ${items}
              <li class="predictive-search__item js-submit" role="option">
                <button type="submit" class="predictive-search__view-all link w-full text-start focus-inset has-ltr-icon js-search-link">
                  View all ${total} match${total === 1 ? '' : 'es'} for “${escapeSearchHtml(searchTerm)}” <span aria-hidden="true">→</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>`;
}

function buildRemanxFaultPredictiveMarkup(matches, searchTerm, limit) {
  const visible = matches.slice(0, limit);
  const total = matches.length;
  const displayCode = tidySearchTerm(searchTerm).toUpperCase();
  const items = visible.map((item) => {
    const image = item.image
      ? `<div class="predictive-result__media media relative"><img src="${escapeSearchHtml(item.image)}" alt="${escapeSearchHtml(item.image_alt || item.title)}" loading="eager" class="predictive-result__media"></div>`
      : '<div class="predictive-result__media media relative"></div>';
    const pricePrefix = item.price_varies ? 'From ' : '';

    return `
      <li class="predictive-search__item" role="option">
        <a class="predictive-result flex items-start focus-inset js-search-link" href="${escapeSearchHtml(item.url)}">
          ${image}
          <div class="predictive-result__info flex-auto">
            <h3 class="predictive-result__title h6">${escapeSearchHtml(item.title)}</h3>
            <div class="predictive-result__sub-title text-sm rmx-part-predictive__match">Fault-code match · ${escapeSearchHtml(displayCode)}</div>
            <div class="price rmx-part-predictive__price">${pricePrefix}${escapeSearchHtml(item.price)}</div>
          </div>
        </a>
      </li>`;
  }).join('');

  return `
    <div class="predictive-search absolute bg-theme-bg text-theme-text text-start rmx-part-predictive rmx-fault-predictive">
      <div class="predictive-search__scroller overflow-auto">
        <div class="predictive-search__results flex flex-col">
          <div class="rmx-part-predictive__head">
            <strong>Fault-code matches</strong>
            <span>${total} found</span>
          </div>
          <div class="predictive-search__tabpanel focus-inset" role="tabpanel" tabindex="0">
            <ul class="predictive-search__list" role="list">
              ${items}
              <li class="predictive-search__item js-submit" role="option">
                <button type="submit" class="predictive-search__view-all link w-full text-start focus-inset has-ltr-icon js-search-link">
                  View all ${total} fault-code match${total === 1 ? '' : 'es'} for “${escapeSearchHtml(searchTerm)}” <span aria-hidden="true">→</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>`;
}

function buildRemanxPartResultCard(item) {
  const image = item.image
    ? `<img src="${escapeSearchHtml(item.image)}" alt="${escapeSearchHtml(item.image_alt || item.title)}" loading="lazy">`
    : '<span class="rmx-part-index-card__placeholder" aria-hidden="true"></span>';
  const pricePrefix = item.price_varies ? 'From ' : '';
  const type = item.product_type ? `<span class="rmx-part-index-card__type">${escapeSearchHtml(item.product_type)}</span>` : '';

  return `
    <article class="rmx-part-index-card">
      <a class="rmx-part-index-card__media" href="${escapeSearchHtml(item.url)}">${image}</a>
      <div class="rmx-part-index-card__body">
        <span class="rmx-part-index-card__match">Part-number match</span>
        <h3><a href="${escapeSearchHtml(item.url)}">${escapeSearchHtml(item.title)}</a></h3>
        ${type}
        <div class="rmx-part-index-card__price">${pricePrefix}${escapeSearchHtml(item.price)}</div>
        <a class="rmx-part-index-card__action" href="${escapeSearchHtml(item.url)}">View repair <span aria-hidden="true">→</span></a>
      </div>
    </article>`;
}

function buildRemanxFaultResultCard(item, searchTerm) {
  const image = item.image
    ? `<img src="${escapeSearchHtml(item.image)}" alt="${escapeSearchHtml(item.image_alt || item.title)}" loading="lazy">`
    : '<span class="rmx-part-index-card__placeholder" aria-hidden="true"></span>';
  const pricePrefix = item.price_varies ? 'From ' : '';
  const type = item.product_type ? `<span class="rmx-part-index-card__type">${escapeSearchHtml(item.product_type)}</span>` : '';

  return `
    <article class="rmx-part-index-card">
      <a class="rmx-part-index-card__media" href="${escapeSearchHtml(item.url)}">${image}</a>
      <div class="rmx-part-index-card__body">
        <span class="rmx-part-index-card__match">Fault-code match · ${escapeSearchHtml(tidySearchTerm(searchTerm).toUpperCase())}</span>
        <h3><a href="${escapeSearchHtml(item.url)}">${escapeSearchHtml(item.title)}</a></h3>
        ${type}
        <div class="rmx-part-index-card__price">${pricePrefix}${escapeSearchHtml(item.price)}</div>
        <a class="rmx-part-index-card__action" href="${escapeSearchHtml(item.url)}">View repair <span aria-hidden="true">→</span></a>
      </div>
    </article>`;
}

function getRemanxSearchPageMount() {
  const catalogue = document.querySelector('[data-rmx-catalogue]');
  if (!catalogue) return null;
  const emptyState = catalogue.querySelector('.rmx-search-state--empty');
  const nativeStatus = catalogue.querySelector(':scope > .rmx-catalogue-status');
  const nativeToolbar = catalogue.querySelector(':scope > .rmx-toolbar-shell');
  const nativeGrid = catalogue.querySelector(':scope > custom-pagination');
  return { catalogue, emptyState, nativeStatus, nativeToolbar, nativeGrid };
}

function activateRemanxSearchPageMount(mount, resultSection) {
  // Only remove native results after successfully resolving the custom index.
  // This avoids duplicate product grids and contradictory result counts.
  [mount.emptyState, mount.nativeStatus, mount.nativeToolbar, mount.nativeGrid]
    .filter(Boolean).forEach((el) => { el.hidden = true; el.style.display = 'none'; });
  mount.catalogue.classList.add('rmx-catalogue--indexed-results');
  mount.catalogue.prepend(resultSection);
}

function attachRemanxSearchPagination(resultSection, matches, renderCard) {
  const grid = resultSection.querySelector('.rmx-part-index-grid');
  const more = resultSection.querySelector('[data-rmx-search-load-more]');
  if (!grid || !more) return;
  let displayed = Math.min(RMX_PART_SEARCH_FULL_LIMIT, matches.length);
  more.addEventListener('click', () => {
    const next = Math.min(displayed + RMX_PART_SEARCH_FULL_LIMIT, matches.length);
    grid.insertAdjacentHTML('beforeend', matches.slice(displayed, next).map(renderCard).join(''));
    displayed = next;
    more.hidden = displayed >= matches.length;
    more.textContent = `Show more (${matches.length - displayed} remaining)`;
  });
}

function renderRemanxFaultSearchPage(matches, searchTerm) {
  const mount = getRemanxSearchPageMount();
  if (!mount) return false;
  const catalogue = mount.catalogue;

  const oldLoading = catalogue.querySelector('[data-rmx-part-search-loading]');
  if (oldLoading) oldLoading.remove();
  const oldResults = catalogue.querySelector('[data-rmx-part-search-page-results]');
  if (oldResults) oldResults.remove();

  const shown = matches.slice(0, RMX_PART_SEARCH_FULL_LIMIT);
  const resultSection = document.createElement('section');
  resultSection.className = 'rmx-part-index-results rmx-fault-index-results';
  resultSection.setAttribute('data-rmx-part-search-page-results', '');
  resultSection.innerHTML = `
    <div class="rmx-catalogue-status rmx-part-index-results__status">
      <span><strong>${matches.length}</strong> repair service${matches.length === 1 ? '' : 's'} matching fault code “${escapeSearchHtml(tidySearchTerm(searchTerm).toUpperCase())}”</span>
      <a href="/pages/repair-request">Send an unlisted part</a>
    </div>
    <div class="rmx-part-index-results__note">
      Matched directly against fault codes listed in Remanx product descriptions.
    </div>
    <div class="rmx-part-index-grid">
      ${shown.map((item) => buildRemanxFaultResultCard(item, searchTerm)).join('')}
    </div>
    ${matches.length > shown.length ? `<button type="button" class="rmx-part-index-results__more" data-rmx-search-load-more>Show more (${matches.length - shown.length} remaining)</button>` : ''}
  `;

  activateRemanxSearchPageMount(mount, resultSection);
  attachRemanxSearchPagination(resultSection, matches, (item) => buildRemanxFaultResultCard(item, searchTerm));

  const hero = document.querySelector('[data-rmx-search-results]');
  if (hero) {
    hero.setAttribute('data-search-result-count', String(matches.length));
    const copy = hero.querySelector('.rmx-v7-catalogue-hero__copy');
    if (copy) {
      let count = copy.querySelector('.rmx-v7-catalogue-hero__count');
      if (!count) {
        count = document.createElement('span');
        count.className = 'rmx-v7-catalogue-hero__count';
        copy.appendChild(count);
      }
      count.textContent = `${matches.length} product result${matches.length === 1 ? '' : 's'}`;
    }
  }

  return true;
}

function renderRemanxPartSearchPage(matches, searchTerm) {
  const mount = getRemanxSearchPageMount();
  if (!mount) return false;
  const catalogue = mount.catalogue;

  const oldLoading = catalogue.querySelector('[data-rmx-part-search-loading]');
  if (oldLoading) oldLoading.remove();
  const oldResults = catalogue.querySelector('[data-rmx-part-search-page-results]');
  if (oldResults) oldResults.remove();

  const shown = matches.slice(0, RMX_PART_SEARCH_FULL_LIMIT);
  const resultSection = document.createElement('section');
  resultSection.className = 'rmx-part-index-results';
  resultSection.setAttribute('data-rmx-part-search-page-results', '');
  resultSection.innerHTML = `
    <div class="rmx-catalogue-status rmx-part-index-results__status">
      <span><strong>${matches.length}</strong> matching repair service${matches.length === 1 ? '' : 's'} for “${escapeSearchHtml(searchTerm)}”</span>
      <a href="/pages/repair-request">Send an unlisted part</a>
    </div>
    <div class="rmx-part-index-results__note">
      Matched directly against part numbers in the Remanx catalogue. Keep typing to narrow the results.
    </div>
    <div class="rmx-part-index-grid">
      ${shown.map(buildRemanxPartResultCard).join('')}
    </div>
    ${matches.length > shown.length ? `<button type="button" class="rmx-part-index-results__more" data-rmx-search-load-more>Show more (${matches.length - shown.length} remaining)</button>` : ''}
  `;

  activateRemanxSearchPageMount(mount, resultSection);
  attachRemanxSearchPagination(resultSection, matches, buildRemanxPartResultCard);

  const hero = document.querySelector('[data-rmx-search-results]');
  if (hero) {
    hero.setAttribute('data-search-result-count', String(matches.length));
    const copy = hero.querySelector('.rmx-v7-catalogue-hero__copy');
    if (copy) {
      let count = copy.querySelector('.rmx-v7-catalogue-hero__count');
      if (!count) {
        count = document.createElement('span');
        count.className = 'rmx-v7-catalogue-hero__count';
        copy.appendChild(count);
      }
      count.textContent = `${matches.length} product result${matches.length === 1 ? '' : 's'}`;
    }
  }

  return true;
}

function showRemanxPartSearchLoading(message = 'Checking matching part numbers…') {
  const catalogue = document.querySelector('[data-rmx-catalogue]');
  const emptyState = catalogue && catalogue.querySelector('.rmx-search-state--empty');
  if (!catalogue || !emptyState) return;
  if (catalogue.querySelector('[data-rmx-part-search-loading]')) return;

  emptyState.hidden = true;
  const loading = document.createElement('div');
  loading.className = 'rmx-part-index-loading';
  loading.setAttribute('data-rmx-part-search-loading', '');
  loading.setAttribute('role', 'status');
  loading.innerHTML = `<span class="rmx-part-index-loading__spinner" aria-hidden="true"></span><span>${escapeSearchHtml(message)}</span>`;
  catalogue.insertBefore(loading, emptyState);
}

function clearRemanxPartSearchLoading(showEmptyState = false) {
  const catalogue = document.querySelector('[data-rmx-catalogue]');
  if (!catalogue) return;
  const loading = catalogue.querySelector('[data-rmx-part-search-loading]');
  if (loading) loading.remove();
  if (showEmptyState) {
    const emptyState = catalogue.querySelector('.rmx-search-state--empty');
    if (emptyState) emptyState.hidden = false;
  }
}

function buildFullSearchCandidates(searchTerm) {
  // Formatting fallbacks remain useful for dotted/spaced/hyphenated references
  // and joined vehicle makes. Uninterrupted partial part numbers are handled by
  // the Remanx catalogue index below instead of relying on Shopify tokenisation.
  return buildSearchCandidates(searchTerm).slice(0, 5);
}

function buildSearchCandidates(searchTerm) {
  const original = tidySearchTerm(searchTerm);
  const candidates = [];
  addUniqueSearchCandidate(candidates, original);

  const makeNormalised = normaliseVehicleMakeSpacing(original);
  addUniqueSearchCandidate(candidates, makeNormalised);

  if (looksLikePartNumber(original)) {
    const groups = original.split(/[\s._\-/]+/).filter(Boolean);
    if (groups.length > 1) {
      // Dot first because Bosch/ATE-style references commonly use this form.
      addUniqueSearchCandidate(candidates, groups.join('.'));
      addUniqueSearchCandidate(candidates, groups.join(''));
      addUniqueSearchCandidate(candidates, groups.join('-'));
      addUniqueSearchCandidate(candidates, groups.join(' '));
      addUniqueSearchCandidate(candidates, groups.join('/'));
    }
  }

  // Keep network use controlled: original plus up to four useful fallbacks.
  return candidates.slice(0, 5);
}

window.RemanxSearchNormaliser = {
  buildCandidates: buildSearchCandidates,
  normaliseVehicleMakeSpacing
};

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      this.overlay = this.querySelector('.js-search-overlay');
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
      if (this.documentPointerDownHandler) {
        document.removeEventListener('pointerdown', this.documentPointerDownHandler, true);
      }
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      const currentQuery = this.getQuery();
      if (
        !currentQuery.length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
        return;
      }

      // If predictive search found the product through a normalised fallback,
      // submit that proven term so the full search results page matches too.
      if (
        this.resolvedSearchTerm
        && this.resolvedFromTerm === currentQuery
        && this.resolvedSearchTerm.toLowerCase() !== currentQuery.toLowerCase()
      ) {
        this.input.value = this.resolvedSearchTerm;
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();
      this.resolvedSearchTerm = searchTerm;
      this.resolvedFromTerm = searchTerm;

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Closes predictive results when the customer clicks anywhere outside this
     * search component. The header intentionally hides the snippet overlay, so
     * a document-level listener is required for natural click-away behaviour.
     * @param {PointerEvent} evt - Pointer event.
     */
    handleDocumentPointerDown(evt) {
      const eventPath = typeof evt.composedPath === 'function' ? evt.composedPath() : [];
      if (eventPath.includes(this) || this.contains(evt.target)) return;
      this.close();
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      this.setLiveRegionLoadingState();

      // Incrementing token prevents a slower fallback request for an earlier
      // keystroke from replacing results for the latest query.
      this.searchRequestId = (this.searchRequestId || 0) + 1;
      const requestId = this.searchRequestId;
      const productType = this.productTypeInput ? this.productTypeInput.value : '';

      // Fault codes are often much shorter than part numbers. For code-shaped
      // queries of four to eight characters, check for an exact code token in
      // product descriptions first so results can be clearly labelled as
      // fault-code matches rather than generic product matches.
      if (shouldUseRemanxFaultIndex(searchTerm)) {
        try {
          const index = await loadRemanxPartSearchIndex();
          if (requestId !== this.searchRequestId || this.getQuery() !== searchTerm) return;

          let matches = findRemanxFaultMatches(index, searchTerm);
          if (productType) {
            const wantedType = productType.toLowerCase();
            matches = matches.filter((item) => (item.product_type || '').toLowerCase() === wantedType);
          }

          if (matches.length) {
            this.resolvedSearchTerm = searchTerm;
            this.resolvedFromTerm = searchTerm;
            this.partIndexActive = true;
            this.renderResults(
              buildRemanxFaultPredictiveMarkup(
                matches,
                searchTerm,
                Math.max(1, Number(theme.settings.pSearchLimit) || 6)
              )
            );
            return;
          }
        } catch (error) {
          console.warn('Remanx fault-code index unavailable', error);
        }
      }

      // Shopify can require the complete value for uninterrupted SKU/barcode-like
      // strings. For part-number-shaped queries of six or more characters, use
      // the Remanx catalogue index first so matches narrow progressively as the
      // customer types rather than disappearing until the final character.
      if (shouldUseRemanxPartIndex(searchTerm)) {
        try {
          const index = await loadRemanxPartSearchIndex();
          if (requestId !== this.searchRequestId || this.getQuery() !== searchTerm) return;

          let matches = findRemanxPartMatches(index, searchTerm);
          if (productType) {
            const wantedType = productType.toLowerCase();
            matches = matches.filter((item) => (item.product_type || '').toLowerCase() === wantedType);
          }

          if (matches.length) {
            this.resolvedSearchTerm = searchTerm;
            this.resolvedFromTerm = searchTerm;
            this.partIndexActive = true;
            this.renderResults(
              buildRemanxPartPredictiveMarkup(
                matches,
                searchTerm,
                Math.max(1, Number(theme.settings.pSearchLimit) || 6)
              )
            );
            return;
          }
        } catch (error) {
          // Native predictive search remains the fallback if the catalogue index
          // cannot be loaded for any reason.
          console.warn('Remanx part-number index unavailable', error);
        }
      }

      this.partIndexActive = false;

      // Body is intentionally included so common faults and part numbers held
      // in the product description remain searchable.
      let searchFields = 'title,product_type,variants.title,vendor,body';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku,variants.barcode';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      const candidates = buildSearchCandidates(searchTerm);
      const queryKey = [productType, searchTerm].join('|').toLowerCase();

      if (this.cachedResults[queryKey]) {
        const cached = this.cachedResults[queryKey];
        this.resolvedSearchTerm = cached.resolvedSearchTerm;
        this.resolvedFromTerm = searchTerm;
        this.renderResults(cached.markup);
        return;
      }

      const buildSearchParams = (candidate) => {
        let searchParams = '';
        if (productType !== '') {
          searchParams =
            `q=product_type:${encodeURIComponent(productType)} AND ${encodeURIComponent(candidate)}`;
        } else {
          searchParams = `q=${encodeURIComponent(candidate)}`;
        }

        searchParams += `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
        searchParams += `&${encodeURIComponent('resources[limit_scope]')}=${
          theme.settings.pSearchLimitScope
        }`;
        searchParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
        searchParams += '&section_id=predictive-search';
        return searchParams;
      };

      let firstMarkup = '';
      let resolvedMarkup = '';
      let resolvedSearchTerm = searchTerm;

      try {
        // Try the customer's exact text first. Only make a fallback request
        // when Shopify returns no product, keeping normal searches lightweight.
        for (const candidate of candidates) {
          const response = await fetch(
            `${theme.routes.predictiveSearch}?${buildSearchParams(candidate)}`
          );
          if (!response.ok) throw new Error(response.status);

          const tmpl = document.createElement('template');
          tmpl.innerHTML = await response.text();

          const resultsEl = tmpl.content.querySelector('#shopify-section-predictive-search');
          if (!resultsEl) continue;

          const hasProduct = Boolean(
            resultsEl.querySelector(
              '.predictive-search__tabpanel[id$="-products"] .predictive-search__item:not(.js-submit)'
            )
          );
          const markup = resultsEl.innerHTML.replace(/psearch/g, this.input.id);

          if (!firstMarkup) firstMarkup = markup;
          if (hasProduct) {
            resolvedMarkup = markup;
            resolvedSearchTerm = candidate;
            break;
          }
        }

        if (requestId !== this.searchRequestId || this.getQuery() !== searchTerm) return;

        const finalMarkup = resolvedMarkup || firstMarkup;
        this.resolvedSearchTerm = resolvedSearchTerm;
        this.resolvedFromTerm = searchTerm;
        this.cachedResults[queryKey] = {
          markup: finalMarkup,
          resolvedSearchTerm
        };
        this.renderResults(finalMarkup);
      } catch (error) {
        if (requestId === this.searchRequestId) this.close();
        throw error;
      }
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      this.results.innerHTML = resultsMarkup;
      this.setAttribute('results', true);
      this.open();
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);
      this.documentPointerDownHandler = this.documentPointerDownHandler
        || this.handleDocumentPointerDown.bind(this);

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
      document.addEventListener('pointerdown', this.documentPointerDownHandler, true);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.removeEventListener('keyup', this.keyupHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
      document.removeEventListener('pointerdown', this.documentPointerDownHandler, true);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}

/**
 * Full search-page fallback.
 *
 * Part-number-shaped queries use the same catalogue index as predictive search
 * when Shopify returns zero native products. Other searches retain the small
 * formatting/make-name fallback chain from the previous Remanx search pass.
 */
async function initRemanxSearchPageFallback() {
  const searchRoot = document.querySelector('[data-rmx-search-results]');
  if (!searchRoot) return;

  const performed = searchRoot.getAttribute('data-search-performed') === 'true';
  const resultCount = Number(searchRoot.getAttribute('data-search-result-count') || 0);
  if (!performed) return;

  const params = new URLSearchParams(window.location.search);

  const cleanInternalParams = (restoreQuery = null) => {
    let changed = false;
    if (restoreQuery && params.get('q') !== restoreQuery) {
      params.set('q', restoreQuery);
      changed = true;
    }
    if (params.has('rmx_original_q')) {
      params.delete('rmx_original_q');
      changed = true;
    }
    if (params.has('rmx_search_variant')) {
      params.delete('rmx_search_variant');
      changed = true;
    }
    if (changed) {
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?${params.toString()}${window.location.hash}`
      );
    }
  };

  // A filtered native result page must keep Shopify's filtering semantics.
  // Unfiltered part-number and fault-code queries check the Remanx index even
  // when Shopify found some products (which may be incomplete partial matches).
  // A zero-result state caused by an active collection filter should not
  // trigger alternate query handling; the customer may simply have narrowed too far.
  if (Array.from(params.keys()).some((key) => key.startsWith('filter.'))) return;

  const currentQuery = tidySearchTerm(params.get('q'));
  if (!currentQuery) return;

  const originalQuery = tidySearchTerm(params.get('rmx_original_q') || currentQuery);
  if (resultCount > 0 && !shouldUseRemanxFaultIndex(originalQuery) && !shouldUseRemanxPartIndex(originalQuery)) {
    cleanInternalParams();
    return;
  }

  // Short fault-code lookup. Shopify will often find these natively, but when
  // it does not, the same catalogue index can still recover an exact code token
  // from the product description and label the result correctly.
  //
  // Important: a 6–8 character OEM part-number fragment can also look like a
  // fault code (for example 1K090737). If no genuine fault-code token is found,
  // do NOT stop here — continue into the progressive part-number lookup below.
  const faultLookupEligible = shouldUseRemanxFaultIndex(originalQuery);
  if (faultLookupEligible) {
    showRemanxPartSearchLoading('Checking matching fault codes…');
    try {
      const index = await loadRemanxPartSearchIndex();
      const matches = findRemanxFaultMatches(index, originalQuery);
      if (matches.length) {
        renderRemanxFaultSearchPage(matches, originalQuery);
        clearRemanxPartSearchLoading(false);
        cleanInternalParams(originalQuery);
        return;
      }
    } catch (error) {
      console.warn('Remanx fault-code search page index unavailable', error);
    }

    // Remove the fault-code loading state before potentially changing the
    // message to a part-number lookup. Keep the empty state hidden for now.
    clearRemanxPartSearchLoading(false);
  }

  // Progressive Remanx part-number lookup. This searches the catalogue content
  // directly, so 6135345603 can match 61353456034 and 1K0907379 can match
  // 1K0907379AD without waiting for Shopify to treat the partial token as valid.
  if (shouldUseRemanxPartIndex(originalQuery)) {
    showRemanxPartSearchLoading();
    try {
      const index = await loadRemanxPartSearchIndex();
      const matches = findRemanxPartMatches(index, originalQuery);
      if (matches.length) {
        renderRemanxPartSearchPage(matches, originalQuery);
        clearRemanxPartSearchLoading(false);
        cleanInternalParams(originalQuery);
        return;
      }
    } catch (error) {
      console.warn('Remanx part-number search page index unavailable', error);
    }

    clearRemanxPartSearchLoading(resultCount === 0);
    cleanInternalParams(originalQuery);
    return;
  }

  // If this was only eligible for the short fault-code route (for example a
  // four- or five-character code) and no code match was found, restore the
  // normal zero-results state. Six-to-eight-character ambiguous queries have
  // already had the part-number route above before reaching this point.
  if (faultLookupEligible) {
    clearRemanxPartSearchLoading(resultCount === 0);
    cleanInternalParams(originalQuery);
    return;
  }

  const candidates = buildFullSearchCandidates(originalQuery);
  if (candidates.length < 2) return;

  const currentAttempt = Math.max(0, Number(params.get('rmx_search_variant') || 0));
  const nextAttempt = currentAttempt + 1;

  // A maximum of three fallbacks avoids redirect chains for genuinely absent
  // products while covering common dot/space/joined-word variants.
  if (nextAttempt >= candidates.length || nextAttempt > 3) return;

  params.set('q', candidates[nextAttempt]);
  params.set('rmx_original_q', originalQuery);
  params.set('rmx_search_variant', String(nextAttempt));
  window.location.replace(`${window.location.pathname}?${params.toString()}${window.location.hash}`);
}

initRemanxSearchPageFallback();
